import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Shield, ClipboardList, TrendingUp, FileText, CheckSquare, Building2, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { taskService } from '../services/taskService';
import logoImage from '../assets/logo.svg';

const baseNavItems = [
  { name: 'Home', path: '/dashboard', icon: Home },
  { name: 'Team', path: '/teams', icon: Shield },
  { name: 'Players', path: '/players', icon: Users },
  { name: 'Assess', path: '/assess', icon: ClipboardList },
  { name: 'Develop', path: '/develop', icon: TrendingUp },
  { name: 'Reports', path: '/reports', icon: FileText },
  { name: 'Tasks', path: '/tasks', icon: CheckSquare },
  { name: 'Club', path: '/club', icon: Building2 },
];

export function Navigation() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [teamPath, setTeamPath] = useState('/teams');
  const [mandatoryPending, setMandatoryPending] = useState(0);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await api.get('/teams');
        const teams = res.data.data || [];
        if (teams.length === 0) {
          setTeamPath('/team/create');
        } else if (teams.length === 1) {
          setTeamPath(`/teams/${teams[0].id}`);
        }
      } catch { /* keep default */ }
    };
    fetchTeams();
  }, []);

  useEffect(() => {
    const fetchTaskCount = async () => {
      try {
        const res = await taskService.getTasks();
        setMandatoryPending(res.meta?.mandatory_pending ?? 0);
      } catch { /* ignore */ }
    };
    fetchTaskCount();
  }, [location.pathname]);

  const navItems = baseNavItems
    .filter((item) => item.name !== 'Club' || user?.role === 'CLUB_ADMIN')
    .map((item) =>
      item.name === 'Team' ? { ...item, path: teamPath } : item
    );

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      <div className="flex items-center h-16 px-6 border-b border-gray-200">
        <img src={logoImage} alt="GamePath" className="h-6" />
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          
          return (
            <React.Fragment key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.name}
                {item.name === 'Tasks' && mandatoryPending > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-green-600 rounded-full">
                    {mandatoryPending}
                  </span>
                )}
              </Link>
              {/* Divider between coach actions and club admin actions */}
              {item.name === 'Tasks' && navItems.some(n => n.name === 'Club') && (
                <div className="my-3 border-t border-gray-200" />
              )}
            </React.Fragment>
          );
        })}
      </nav>
      <div className="p-4 border-t border-gray-200">
        <Link 
          to="/profile"
          className="flex items-center gap-3 mb-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-blue-700">
              {user?.profile.firstName?.[0]}{user?.profile.lastName?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.profile.firstName} {user?.profile.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">Coach</p>
          </div>
        </Link>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
